import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "../../api";
import userAction from "../../redux/users/action";
import { useAlert } from "react-alert";

const STAGES = [
  { kicker: "STAGE 01", title: "Beginner Lectures", chip: "Required" },
  { kicker: "STAGE 02", title: "Advanced Lecture", chip: "Required" },
  { kicker: "STAGE 03", title: "Demo Trading", chip: "Coming Soon" },
];

const SKIP_RISKS = [
  "Cryptocurrency trading involves significant risk.",
  "You are solely responsible for every trade you take.",
  "WaqarZaka.net does not guarantee profits or investment returns.",
  "We strongly recommend completing the educational program before using trading signals.",
];

/* ── inline styles ── */
const S = {
  stage: {
    minHeight: "100vh",
    background:
      "radial-gradient(1100px 560px at 12% -12%,#efe8ff 0%,rgba(239,232,255,0) 58%),radial-gradient(900px 500px at 100% 2%,#e6f3ff 0%,rgba(230,243,255,0) 55%),#f6f5fb",
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    color: "#141020",
  },
  pad: { maxWidth: 1100, margin: "0 auto", padding: "40px 24px 80px" },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 13,
    background: "linear-gradient(135deg,#a78bfa,#7c3aed)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 17,
    color: "#fff",
    boxShadow: "0 10px 26px rgba(124,61,237,.45)",
    fontFamily: "monospace",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    background: "#f2eefe",
    border: "1px solid #e0d5fb",
    color: "#7c3aed",
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: ".22em",
    textTransform: "uppercase",
    padding: "8px 16px",
    borderRadius: 999,
  },
  primary: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    padding: "18px 34px",
    borderRadius: 16,
    background: "linear-gradient(135deg,#8b5cf6,#6d28d9 55%,#4c1d95)",
    boxShadow: "0 16px 40px rgba(124,61,237,.42)",
    transition: "all .22s ease",
  },
  ghost: {
    fontSize: 14.5,
    fontWeight: 800,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "#7c4dff",
    background: "transparent",
    border: "1px solid #e5e0f2",
    cursor: "pointer",
    padding: "18px 26px",
    borderRadius: 16,
    transition: "all .2s ease",
  },
  card: {
    background: "linear-gradient(165deg,#fff,#faf9fe)",
    border: "1px solid #eeebf6",
    borderRadius: 22,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 22,
    background: "rgba(20,10,40,.5)",
    backdropFilter: "blur(10px)",
  },
  modal: {
    width: "100%",
    maxWidth: 560,
    maxHeight: "92vh",
    borderRadius: 28,
    overflow: "hidden",
    border: "1px solid rgba(248,113,113,.36)",
    boxShadow: "0 44px 110px rgba(20,10,40,.32)",
  },
  modalInner: {
    overflowY: "auto",
    maxHeight: "92vh",
    background: "linear-gradient(165deg,#fff,#fff6f6)",
  },
};

export default function OnboardingCourse() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setUser } = userAction;
  const userStore = useSelector((state) => state.userReducer);
  const alert = useAlert();
  const [screen, setScreen] = useState("landing"); // landing | roadmap | courses | demo
  const [warningOpen, setWarningOpen] = useState(false);
  const [courseStatus, setCourseStatus] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [demoStatus, setDemoStatus] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [certStatus, setCertStatus] = useState(null); // null | 'none' | 'pending' | 'approved'
  const [certLoading, setCertLoading] = useState(false);
  const [demoForm, setDemoForm] = useState({
    direction: null,
    confidence: 65,
    analysis: "",
  });
  const [btcPrice, setBtcPrice] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "sz-onboarding-css";
    style.textContent = `
      @keyframes szFade { from { opacity:0 } to { opacity:1 } }
      @keyframes szPing { 0%{transform:scale(1);opacity:.5} 70%{transform:scale(2.6);opacity:0} 100%{opacity:0} }
      @keyframes szToastIn { from { opacity:0; transform:translate(-50%,-50%) scale(.88); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }
      @keyframes szToastBar { from { width:100%; } to { width:0%; } }
      .sz-video-modal iframe { width:100% !important; height:100% !important; border:none !important; display:block; }
      .sz-btn-primary { transition: all .22s ease; }
      .sz-btn-primary:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 24px 56px rgba(124,61,237,.58) !important; filter: brightness(1.07); }
      .sz-btn-primary:active:not(:disabled) { transform: translateY(0); }
      .sz-btn-ghost { transition: all .2s ease; }
      .sz-btn-ghost:hover { background: rgba(124,61,237,.07) !important; border-color: #c4b5fd !important; color: #6d28d9 !important; }
      .sz-btn-skip { transition: all .2s ease; }
      .sz-btn-skip:hover { background: rgba(248,113,113,.16) !important; border-color: rgba(248,113,113,.65) !important; }
      .sz-btn-green { transition: all .22s ease; }
      .sz-btn-green:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 20px 48px rgba(5,150,105,.52) !important; filter: brightness(1.07); }
      .sz-btn-green:active:not(:disabled) { transform: translateY(0); }
      .sz-btn-rewatch { transition: all .18s ease; }
      .sz-btn-rewatch:hover { background: rgba(124,61,237,.15) !important; border-color: rgba(124,61,237,.42) !important; }
      .sz-btn-modal-close { transition: all .18s ease; }
      .sz-btn-modal-close:hover { background: rgba(255,255,255,.15) !important; border-color: rgba(255,255,255,.3) !important; }
      .sz-btn-risk { transition: all .2s ease; }
      .sz-btn-risk:hover:not(:disabled) { background: rgba(220,38,38,.06) !important; border-color: rgba(248,113,113,.45) !important; color: #dc2626 !important; }
      .sz-btn-back { transition: all .18s ease; }
      .sz-btn-back:hover { background: rgba(124,61,237,.07) !important; border-color: #c4b5fd !important; color: #7c3aed !important; }
      .sz-dir-btn:hover { transform: scale(1.025); }
    `;
    document.head.appendChild(style);
    return () => {
      document.getElementById("sz-onboarding-css")?.remove();
    };
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.getOnboardingStatus();
      // Skip course → go straight to dashboard
      if (resp?.continueWithoutCourse) {
        navigate("/dashboard");
        return;
      }
      // Course complete + demo unlocked → dashboard
      if (resp?.userCompletedCourse && resp?.demoTradingUnlocked) {
        navigate("/dashboard");
        return;
      }
      setCourseStatus(resp);
    } catch (e) {
      setError("Failed to load course videos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (screen === "courses" || screen === "roadmap") loadStatus();
    if (screen === "demo") loadDemoStatus();
  }, [screen]);

  // Binance WebSocket for live BTC price on demo screen
  useEffect(() => {
    if (screen !== "demo") return;
    let ws;
    try {
      ws = new WebSocket("wss://stream.binance.com:9443/ws/btcusdt@trade");
      ws.onmessage = (e) => {
        try {
          setBtcPrice(parseFloat(JSON.parse(e.data).p));
        } catch (_) {}
      };
    } catch (_) {}
    return () => {
      try {
        ws && ws.close();
      } catch (_) {}
    };
  }, [screen]);

  const handleMarkWatched = async (category, video) => {
    if (!video) return;
    setActionLoading(true);
    try {
      await api.markVideoWatched({ videoId: video._id, category });
      setVideoModal(null);
      await loadStatus(); // reload to get next video in sequence
    } catch (e) {
      alert.show("Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const updateReduxUser = (patch) => {
    const updatedData = {
      ...userStore?.users,
      user: { ...userStore?.users?.user, ...patch },
    };
    dispatch(setUser(updatedData));
  };

  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await api.completeOnboarding();
      updateReduxUser({ userCompletedCourse: true });
      navigate("/dashboard");
    } catch (e) {
      alert.show("Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkip = async () => {
    setActionLoading(true);
    try {
      await api.skipOnboarding();
      updateReduxUser({ continueWithoutCourse: true });
      navigate("/dashboard");
    } catch (e) {
      alert.show("Something went wrong. Please try again.");
      setActionLoading(false);
    }
  };

  const loadCertStatus = async () => {
    try {
      const resp = await api.getCertificationStatus();
      setCertStatus(resp?.status || "none");
    } catch (_) {}
  };

  const handleRequestCertification = async () => {
    setCertLoading(true);
    try {
      const resp = await api.requestCertification();
      if (resp?.success) setCertStatus("pending");
      else alert.show(resp?.message || "Something went wrong");
    } catch (e) {
      alert.show(e?.message || "Something went wrong");
    } finally {
      setCertLoading(false);
    }
  };

  const loadDemoStatus = async () => {
    setDemoLoading(true);
    try {
      const resp = await api.getDemoStatus();
      setDemoStatus(resp);
      if (resp?.unlocked) {
        updateReduxUser({ demoTradingUnlocked: true });
        loadCertStatus();
      }
    } catch (_) {
    } finally {
      setDemoLoading(false);
    }
  };

  const handleDemoSubmit = async () => {
    setActionLoading(true);
    try {
      await api.submitDemoPrediction({
        direction: demoForm.direction,
        confidence: demoForm.confidence,
        analysis: demoForm.analysis,
      });
      setDemoForm({ direction: null, confidence: 65, analysis: "" });
      await loadDemoStatus();
      showToast(
        "Analysis submitted! Check back in a few hours for your result.",
      );
    } catch (e) {
      alert.show(e?.message || "Something went wrong. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryDemo = async () => {
    setActionLoading(true);
    try {
      await api.retryDemoSession();
      setDemoStatus(null);
      setDemoForm({ direction: null, confidence: 65, analysis: "" });
      await loadDemoStatus();
    } catch (e) {
      alert.show(e?.message || "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ── screens ── */
  const renderLanding = () => (
    <div style={{ animation: "szFade .5s ease both" }}>
      <div style={{ textAlign: "center", marginBottom: 52 }}>
        <div style={{ ...S.pill, marginBottom: 22 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#10b981",
              boxShadow: "0 0 10px rgba(16,185,129,.8)",
            }}
          />
          First-time setup
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: "clamp(34px,5vw,58px)",
            fontWeight: 800,
            lineHeight: 1.04,
            letterSpacing: "-.035em",
          }}
        >
          Welcome to{" "}
          <span
            style={{
              background: "linear-gradient(100deg,#7c3aed,#6d28d9 46%,#2563eb)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Waqar Zaka Trades
          </span>
        </h1>
        <p
          style={{
            margin: "16px auto 0",
            maxWidth: 500,
            fontSize: 16,
            lineHeight: 1.6,
            color: "#6a6580",
          }}
        >
          Choose how you would like to begin your crypto journey.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
          gap: 24,
        }}
      >
        {/* Learn First */}
        <div
          style={{
            position: "relative",
            borderRadius: 30,
            padding: "2px",
            background:
              "linear-gradient(120deg,#4f46e5,#a78bfa,#38bdf8,#7c3aed,#4f46e5)",
            backgroundSize: "300% 300%",
            boxShadow: "0 30px 76px rgba(93,45,214,.2)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -13,
              right: 24,
              zIndex: 4,
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: "#2a1055",
              background: "linear-gradient(135deg,#fbbf24,#f59e0b)",
              borderRadius: 999,
              padding: "7px 14px",
              boxShadow: "0 10px 24px rgba(245,158,11,.4)",
            }}
          >
            ⭐ Recommended
          </div>
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 28,
              padding: "36px 34px 34px",
              background: "linear-gradient(165deg,#fff,#faf9fe)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <div style={{ fontSize: 52, marginBottom: 20 }}>🎓</div>
            <h2
              style={{
                margin: "0 0 12px",
                fontSize: 28,
                fontWeight: 800,
                letterSpacing: "-.025em",
              }}
            >
              Learn First
            </h2>
            <p
              style={{
                margin: "0 0 22px",
                fontSize: 15,
                lineHeight: 1.62,
                color: "#6a6580",
              }}
            >
              "I'm new to crypto and I want to learn before following
              professional signals."
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 9,
                marginBottom: 28,
              }}
            >
              {[
                "Beginner and advanced lecture tracks",
                "Both watched in full before you trade",
                "Unlock Futures signals on completion",
              ].map((p) => (
                <div
                  key={p}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13.5,
                    color: "#3a3552",
                  }}
                >
                  <span
                    style={{
                      width: 19,
                      height: 19,
                      borderRadius: 6,
                      background: "rgba(16,185,129,.12)",
                      border: "1px solid rgba(16,185,129,.36)",
                      color: "#059669",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                  {p}
                </div>
              ))}
            </div>
            <button
              className="sz-btn-primary"
              style={{ ...S.primary, width: "100%", marginTop: "auto" }}
              onClick={() => setScreen("roadmap")}
            >
              Start Learning →
            </button>
          </div>
        </div>

        {/* Skip Learning */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 30,
            padding: "36px 34px 34px",
            background: "linear-gradient(165deg,#fffaf9,#fff1f1)",
            border: "1px solid rgba(248,113,113,.26)",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 60px rgba(31,20,66,.1)",
          }}
        >
          <div style={{ fontSize: 52, marginBottom: 20 }}>⚠️</div>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: "-.025em",
            }}
          >
            Skip Learning
          </h2>
          <p
            style={{
              margin: "0 0 22px",
              fontSize: 15,
              lineHeight: 1.62,
              color: "#8a5560",
            }}
          >
            "I don't want to complete the educational program. I understand the
            risks involved."
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 9,
              marginBottom: 28,
            }}
          >
            {[
              "Cryptocurrency trading involves significant risk.",
              "You are solely responsible for every trade you take.",
              "No certificate, no evaluation.",
            ].map((r) => (
              <div
                key={r}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "#7c5560",
                }}
              >
                <span
                  style={{
                    width: 19,
                    height: 19,
                    borderRadius: 6,
                    background: "rgba(248,113,113,.12)",
                    border: "1px solid rgba(248,113,113,.36)",
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  !
                </span>
                {r}
              </div>
            ))}
          </div>
          <button
            className="sz-btn-skip"
            style={{
              width: "100%",
              marginTop: "auto",
              fontSize: 14.5,
              fontWeight: 800,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              textAlign: "center",
              color: "#dc2626",
              background: "rgba(248,113,113,.08)",
              border: "1px solid rgba(248,113,113,.38)",
              cursor: "pointer",
              padding: 18,
              borderRadius: 16,
            }}
            onClick={() => setWarningOpen(true)}
          >
            Skip Anyway
          </button>
        </div>
      </div>
    </div>
  );

  const renderRoadmap = () => {
    const s = courseStatus;
    const allBegDone = s
      ? s.beginnerWatched >= s.beginnerTotal && s.beginnerTotal > 0
      : false;
    const allAdvDone = s
      ? s.advanceWatched >= s.advanceTotal && s.advanceTotal > 0
      : false;
    // 0 = beginner active, 1 = advance active, 2 = all done
    const activeIdx = allBegDone ? (allAdvDone ? 2 : 1) : 0;

    const ctaLabel = allAdvDone
      ? "Start Demo Trading →"
      : allBegDone
        ? "Continue Advanced Lectures →"
        : allBegDone === false && s?.beginnerWatched > 0
          ? "Resume Beginner Lectures →"
          : "Start Beginner Lectures →";

    return (
      <div>
        <div style={{ maxWidth: 600, marginBottom: 44 }}>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(30px,4vw,46px)",
              fontWeight: 800,
              letterSpacing: "-.032em",
              lineHeight: 1.06,
            }}
          >
            Your Learning{" "}
            <span
              style={{
                background:
                  "linear-gradient(100deg,#7c3aed,#6d28d9 46%,#2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Journey
            </span>
          </h1>
          <p
            style={{
              margin: "14px 0 0",
              fontSize: 15.5,
              lineHeight: 1.6,
              color: "#6a6580",
            }}
          >
            Two lecture tracks. Each stage unlocks the next. Complete both to
            access Futures signals.
          </p>
        </div>

        <div style={{ position: "relative", paddingLeft: 8 }}>
          <div
            style={{
              position: "absolute",
              left: 35,
              top: 12,
              bottom: 12,
              width: 2,
              borderRadius: 999,
              background: "linear-gradient(180deg,#7c3aed,#38bdf8,#34d399)",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {STAGES.map((st, i) => {
              const isCurrent = i === activeIdx;
              const isDone = i < activeIdx;
              const isLocked = i === 2 && !allAdvDone; // Demo Trading locked until all videos watched
              return (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 22,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      flexShrink: 0,
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "monospace",
                      fontSize: 13,
                      fontWeight: 800,
                      background: "#fff",
                      color: isDone
                        ? "#059669"
                        : isCurrent
                          ? "#7c3aed"
                          : "#b0aac4",
                      border: isDone
                        ? "2px solid rgba(16,185,129,.5)"
                        : isCurrent
                          ? "2px solid #a78bfa"
                          : "1px solid #eeebf6",
                      boxShadow: isCurrent
                        ? "0 0 24px rgba(167,139,250,.35)"
                        : isDone
                          ? "0 0 16px rgba(16,185,129,.2)"
                          : "none",
                    }}
                  >
                    {isCurrent && (
                      <span
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          background: "#a78bfa",
                          animation: "szPing 2.4s ease-out infinite",
                          opacity: 0.5,
                        }}
                      />
                    )}
                    <span style={{ position: "relative", zIndex: 2 }}>
                      {isDone ? "✓" : String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div
                    onClick={
                      i === 2 && allAdvDone
                        ? () => setScreen("demo")
                        : undefined
                    }
                    style={{
                      flex: 1,
                      borderRadius: 18,
                      padding: "18px 22px",
                      cursor: i === 2 && allAdvDone ? "pointer" : "default",
                      background: isDone
                        ? "linear-gradient(120deg,#f0fdf8,#ecfdf5)"
                        : isCurrent
                          ? "linear-gradient(120deg,#f6f2ff,#eef5ff)"
                          : "#fbfbfd",
                      border: isDone
                        ? "1px solid rgba(16,185,129,.3)"
                        : isCurrent
                          ? "1px solid rgba(167,139,250,.5)"
                          : "1px solid #eeebf6",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontSize: 9.5,
                            fontWeight: 800,
                            letterSpacing: ".18em",
                            textTransform: "uppercase",
                            color: "#9a94ad",
                            marginBottom: 5,
                          }}
                        >
                          {st.kicker}
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 800,
                            letterSpacing: "-.015em",
                            color: isLocked ? "#8a86a0" : "#141020",
                          }}
                        >
                          {st.title}
                        </div>
                      </div>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: ".12em",
                          textTransform: "uppercase",
                          borderRadius: 999,
                          padding: "6px 11px",
                          color: isDone
                            ? "#059669"
                            : isCurrent
                              ? "#7c3aed"
                              : isLocked
                                ? "#a9a4bd"
                                : "#a9a4bd",
                          background: isDone
                            ? "rgba(16,185,129,.1)"
                            : isCurrent
                              ? "#f2eefe"
                              : "#fbfbfd",
                          border: isDone
                            ? "1px solid rgba(16,185,129,.3)"
                            : isCurrent
                              ? "1px solid #e0d5fb"
                              : "1px solid #eeebf6",
                        }}
                      >
                        {isDone
                          ? "✓ Completed"
                          : i === 2 && allAdvDone
                            ? "Start →"
                            : st.chip}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{ marginTop: 38, display: "flex", gap: 14, flexWrap: "wrap" }}
        >
          <button
            className="sz-btn-primary"
            style={S.primary}
            onClick={() => setScreen(allAdvDone ? "demo" : "courses")}
          >
            {ctaLabel}
          </button>
          <button
            className="sz-btn-ghost"
            style={S.ghost}
            onClick={() => setScreen("landing")}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  };

  const renderCourses = () => {
    const s = courseStatus;
    const allBegDone = s ? s.beginnerWatched >= s.beginnerTotal : false;
    const allAdvDone = s ? s.advanceWatched >= s.advanceTotal : false;
    const totalDone = (s?.beginnerWatched ?? 0) + (s?.advanceWatched ?? 0);
    const totalVideos = (s?.beginnerTotal ?? 0) + (s?.advanceTotal ?? 0);

    // show advance section only after all beginner videos are watched
    const showAdvance = allBegDone;
    const canComplete = allBegDone && allAdvDone;

    const VideoCard = ({ category, video }) => {
      const isBeginner = category === "beginner";
      const watched = isBeginner
        ? (s?.beginnerWatched ?? 0)
        : (s?.advanceWatched ?? 0);
      const total = isBeginner
        ? (s?.beginnerTotal ?? 0)
        : (s?.advanceTotal ?? 0);
      const allDone = watched >= total;
      const tint = isBeginner
        ? "linear-gradient(150deg,#4c1d95,#1e1b4b)"
        : "linear-gradient(150deg,#0e7490,#0b2c52)";
      const emoji = isBeginner ? "🌱" : "🚀";
      const sectionLabel = isBeginner ? "BEGINNER" : "ADVANCED";

      return (
        <div
          style={{
            borderRadius: 22,
            overflow: "hidden",
            background: "linear-gradient(165deg,#fff,#faf9fe)",
            border: `1px solid ${allDone ? "rgba(16,185,129,.34)" : "#eeebf6"}`,
            boxShadow: "0 12px 34px rgba(31,20,66,.07)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* thumbnail */}
          <div
            style={{
              position: "relative",
              aspectRatio: "16/9",
              background: tint,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 42 }}>{emoji}</span>
            <span
              style={{
                position: "absolute",
                top: 12,
                left: 12,
                fontFamily: "monospace",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".12em",
                color: "#fff",
                background: "rgba(8,6,16,.66)",
                border: "1px solid rgba(255,255,255,.18)",
                borderRadius: 999,
                padding: "5px 10px",
              }}
            >
              {sectionLabel}
            </span>
            <span
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".1em",
                textTransform: "uppercase",
                borderRadius: 999,
                padding: "5px 10px",
                color: allDone ? "#6ee7b7" : "#c4b5fd",
                background: allDone
                  ? "rgba(5,150,105,.28)"
                  : "rgba(124,61,237,.28)",
                border: allDone
                  ? "1px solid rgba(52,211,153,.44)"
                  : "1px solid rgba(167,139,250,.44)",
              }}
            >
              {allDone
                ? `✓ ${watched}/${total} Done`
                : `${watched}/${total} Watched`}
            </span>
          </div>

          {/* body */}
          <div
            style={{
              padding: "20px 22px 22px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                fontSize: 16.5,
                fontWeight: 800,
                letterSpacing: "-.015em",
                lineHeight: 1.24,
              }}
            >
              {isBeginner ? "Beginner Lectures" : "Advanced Lectures"}
            </div>
            {video && !allDone && (
              <div
                style={{ fontSize: 13, color: "#6a6580", fontStyle: "italic" }}
              >
                Next:{" "}
                {video.title ||
                  (isBeginner
                    ? `Beginner Video #${watched + 1}`
                    : `Advanced Video #${watched + 1}`)}
              </div>
            )}

            {/* progress bar */}
            <div
              style={{
                height: 6,
                borderRadius: 99,
                background: "#f0edf8",
                overflow: "hidden",
                marginTop: 4,
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 99,
                  width:
                    total > 0
                      ? `${Math.round((watched / total) * 100)}%`
                      : "0%",
                  background: allDone
                    ? "linear-gradient(90deg,#059669,#34d399)"
                    : "linear-gradient(90deg,#7c3aed,#a78bfa)",
                  transition: "width .5s ease",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: "auto",
                paddingTop: 6,
                flexWrap: "wrap",
              }}
            >
              {video && !allDone && (
                <button
                  className="sz-btn-primary"
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    borderRadius: 11,
                    padding: "10px 16px",
                    color: "#fff",
                    background: "linear-gradient(135deg,#8b5cf6,#6d28d9)",
                    border: "none",
                    cursor: "pointer",
                    boxShadow: "0 8px 20px rgba(124,61,237,.4)",
                  }}
                  onClick={() => setVideoModal({ category, video })}
                >
                  ▶ Watch Next
                </button>
              )}
              {allDone && (
                <span
                  style={{ fontSize: 12.5, fontWeight: 800, color: "#059669" }}
                >
                  ✓ All Completed
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 32,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "clamp(28px,3.8vw,44px)",
                fontWeight: 800,
                letterSpacing: "-.032em",
                lineHeight: 1.06,
              }}
            >
              Lecture{" "}
              <span
                style={{
                  background: "linear-gradient(100deg,#7c3aed,#2563eb)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Library
              </span>
            </h1>
            <p style={{ margin: "10px 0 0", fontSize: 14.5, color: "#6a6580" }}>
              Complete all beginner lectures first, then advance lectures
              unlock.
            </p>
          </div>
          {s && (
            <div
              style={{
                fontFamily: "monospace",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".12em",
                color: "#7c3aed",
              }}
            >
              {totalDone} / {totalVideos} COMPLETE
            </div>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#6a6580" }}>
            <div
              className="spinner-border"
              role="status"
              style={{
                width: 32,
                height: 32,
                borderWidth: 3,
                color: "#7c3aed",
                marginBottom: 16,
              }}
            />
            <div
              style={{ fontSize: 15, fontWeight: 700, letterSpacing: ".01em" }}
            >
              Loading course videos…
            </div>
          </div>
        )}
        {error && (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: "#dc2626",
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && s && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                gap: 20,
                marginBottom: 24,
              }}
            >
              <VideoCard category="beginner" video={s.currentBeginner} />
              {showAdvance && (
                <VideoCard category="advance" video={s.currentAdvance} />
              )}
            </div>

            {!showAdvance && (
              <p style={{ fontSize: 13, color: "#9a94ad", marginBottom: 16 }}>
                Complete all {s.beginnerTotal} beginner lectures to unlock
                Advanced.
              </p>
            )}

            {/* Previously watched rewatch lists */}
            {[
              {
                label: "Beginner",
                videos: s.watchedBeginnerVideos,
                category: "beginner",
              },
              ...(showAdvance
                ? [
                    {
                      label: "Advanced",
                      videos: s.watchedAdvanceVideos,
                      category: "advance",
                    },
                  ]
                : []),
            ].map(({ label, videos: wvids, category: cat }) =>
              wvids && wvids.length > 0 ? (
                <div key={cat} style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "#9a94ad",
                      marginBottom: 10,
                    }}
                  >
                    {label} — Previously Watched ({wvids.length})
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {wvids.map((vid, i) => (
                      <div
                        key={vid._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          padding: "12px 16px",
                          borderRadius: 14,
                          background: "#fff",
                          border: "1px solid #eeebf6",
                          boxShadow: "0 2px 8px rgba(31,20,66,.04)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            minWidth: 0,
                          }}
                        >
                          <span
                            style={{
                              flexShrink: 0,
                              width: 28,
                              height: 28,
                              borderRadius: 8,
                              background: "rgba(16,185,129,.12)",
                              border: "1px solid rgba(16,185,129,.3)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              color: "#059669",
                              fontWeight: 800,
                            }}
                          >
                            ✓
                          </span>
                          <span
                            style={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: "#2d2540",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {vid.title || `${label} Video #${i + 1}`}
                          </span>
                        </div>
                        <button
                          className="sz-btn-rewatch"
                          style={{
                            flexShrink: 0,
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: ".07em",
                            textTransform: "uppercase",
                            borderRadius: 9,
                            padding: "7px 14px",
                            color: "#7c3aed",
                            background: "rgba(124,61,237,.08)",
                            border: "1px solid rgba(124,61,237,.22)",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setVideoModal({ category: cat, video: vid })
                          }
                        >
                          ▶ Rewatch
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null,
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              {userStore?.users?.user?.demoTradingUnlocked ? (
                <button
                  className="sz-btn-primary"
                  style={S.primary}
                  onClick={() => navigate("/dashboard")}
                >
                  Enter Futures Signals →
                </button>
              ) : (
                <button
                  className="sz-btn-primary"
                  disabled={!canComplete}
                  style={{
                    ...S.primary,
                    opacity: canComplete ? 1 : 0.45,
                    cursor: canComplete ? "pointer" : "not-allowed",
                  }}
                  onClick={() => setScreen("demo")}
                >
                  Start Demo Trading →
                </button>
              )}
              {!canComplete && (
                <span style={{ fontSize: 12.5, color: "#9a94ad" }}>
                  {!allBegDone
                    ? `${s.beginnerTotal - s.beginnerWatched} beginner lecture(s) remaining`
                    : `${s.advanceTotal - s.advanceWatched} advanced lecture(s) remaining`}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderVideoModal = () => {
    if (!videoModal) return null;
    const { category, video } = videoModal;
    const watchedList =
      category === "beginner"
        ? (courseStatus?.watchedBeginnerVideos ?? [])
        : (courseStatus?.watchedAdvanceVideos ?? []);
    const isAlreadyWatched = watchedList.some((v) => v._id === video?._id);
    const lectureNum = video?.order ?? watchedList.length + 1;
    const lectureLabel = `${category === "beginner" ? "BEGINNER" : "ADVANCED"} · LECTURE ${String(lectureNum).padStart(2, "0")}`;
    const title =
      video?.title ||
      (category === "beginner" ? "Beginner Lecture" : "Advanced Lecture");
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 80,
          background: "rgba(10,6,30,.82)",
          backdropFilter: "blur(14px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px 16px",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) setVideoModal(null);
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 900,
            background: "#13102a",
            borderRadius: 24,
            border: "1px solid rgba(124,61,237,.3)",
            boxShadow: "0 40px 100px rgba(0,0,0,.7)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* modal header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 24px",
              borderBottom: "1px solid rgba(255,255,255,.08)",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#a78bfa",
                  marginBottom: 4,
                }}
              >
                {lectureLabel}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>
                {title}
              </div>
            </div>
            <button
              className="sz-btn-modal-close"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,.15)",
                background: "rgba(255,255,255,.07)",
                color: "#fff",
                fontSize: 18,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setVideoModal(null)}
            >
              ✕
            </button>
          </div>

          {/* video */}
          <div
            className="sz-video-modal"
            style={{ width: "100%", aspectRatio: "16/9", background: "#000" }}
            dangerouslySetInnerHTML={{ __html: video?.video || "" }}
          />

          {/* modal footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
              padding: "18px 24px",
              borderTop: "1px solid rgba(255,255,255,.08)",
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: "#a09ab8" }}>
              {isAlreadyWatched
                ? "You have already completed this lecture."
                : "Watch the full lecture, then mark it as watched to continue."}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="sz-btn-modal-close"
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  borderRadius: 12,
                  padding: "12px 22px",
                  color: "#a09ab8",
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.12)",
                  cursor: "pointer",
                }}
                onClick={() => setVideoModal(null)}
              >
                Close
              </button>
              {!isAlreadyWatched && (
                <button
                  className="sz-btn-green"
                  disabled={actionLoading}
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    borderRadius: 12,
                    padding: "12px 22px",
                    color: "#fff",
                    background: "linear-gradient(135deg,#059669,#047857)",
                    border: "none",
                    cursor: actionLoading ? "not-allowed" : "pointer",
                    boxShadow: "0 8px 22px rgba(5,150,105,.4)",
                    opacity: actionLoading ? 0.7 : 1,
                  }}
                  onClick={() => handleMarkWatched(category, video)}
                >
                  {actionLoading ? "Saving…" : "✓ Mark as Watched"}
                </button>
              )}
              {isAlreadyWatched && (
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#34d399",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  ✓ Completed
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWarningModal = () => (
    <div
      style={S.overlay}
      onClick={(e) => e.target === e.currentTarget && setWarningOpen(false)}
    >
      <div style={S.modal}>
        <div style={S.modalInner}>
          <div
            style={{
              padding: "20px 28px 18px",
              borderBottom: "1px solid #f0edf8",
              position: "relative",
            }}
          >
            <button
              className="sz-btn-modal-close"
              onClick={() => setWarningOpen(false)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 34,
                height: 34,
                borderRadius: 9,
                border: "1px solid #eeebf6",
                background: "#f6f5fb",
                color: "#9a94ad",
                fontSize: 16,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  position: "relative",
                  width: 58,
                  height: 58,
                  borderRadius: 18,
                  background: "rgba(248,113,113,.12)",
                  border: "1px solid rgba(248,113,113,.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                  flexShrink: 0,
                }}
              >
                ⚠️
              </div>
              <div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: ".2em",
                    textTransform: "uppercase",
                    color: "#dc2626",
                    marginBottom: 6,
                  }}
                >
                  Important Warning
                </div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: 20,
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  You have chosen to skip the educational program.
                </h2>
              </div>
            </div>
          </div>

          <div style={{ padding: "16px 28px 6px" }}>
            <p style={{ margin: "0 0 12px", fontSize: 14.5, color: "#6a6580" }}>
              By continuing, you acknowledge that:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {SKIP_RISKS.map((r) => (
                <div
                  key={r}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "10px 14px",
                    borderRadius: 14,
                    background: "rgba(248,113,113,.05)",
                    border: "1px solid rgba(248,113,113,.18)",
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#dc2626",
                      flexShrink: 0,
                      marginTop: 7,
                    }}
                  />
                  <span
                    style={{ fontSize: 14, lineHeight: 1.55, color: "#4a4560" }}
                  >
                    {r}
                  </span>
                </div>
              ))}
            </div>
            <p
              style={{
                margin: "14px 0 0",
                fontSize: 15.5,
                fontWeight: 800,
                textAlign: "center",
                color: "#141020",
              }}
            >
              Are you sure you want to continue without learning?
            </p>
          </div>

          <div
            style={{
              padding: "14px 28px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <button
              className="sz-btn-primary"
              style={{ ...S.primary, width: "100%", justifyContent: "center" }}
              onClick={() => setWarningOpen(false)}
            >
              ← Go Back & Learn
            </button>
            <button
              className="sz-btn-risk"
              disabled={actionLoading}
              style={{
                width: "100%",
                textAlign: "center",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "#8a86a0",
                background: "transparent",
                border: "1px solid #eeebf6",
                cursor: actionLoading ? "not-allowed" : "pointer",
                padding: 16,
                borderRadius: 14,
              }}
              onClick={handleSkip}
            >
              {actionLoading
                ? "Please wait…"
                : "Continue Anyway — I Accept All Risks"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDemo = () => {
    if (demoLoading)
      return (
        <div style={{ textAlign: "center", padding: 80, color: "#6a6580" }}>
          <div
            className="spinner-border"
            role="status"
            style={{
              width: 36,
              height: 36,
              borderWidth: 3,
              color: "#7c3aed",
              marginBottom: 18,
            }}
          />
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Loading your demo trading data…
          </div>
        </div>
      );
    const s = demoStatus;
    const fmtPrice = (n) =>
      n
        ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
        : "—";
    const livePrice = btcPrice || s?.btcPrice;
    const isSubmitLocked =
      !demoForm.direction ||
      demoForm.analysis.trim().length < 12 ||
      s?.submittedToday ||
      actionLoading;

    const weekProgress = (weekNum) => {
      const start = (weekNum - 1) * 7 + 1;
      const end = weekNum * 7;
      const resolved = (s?.trades ?? []).filter(
        (t) =>
          t.dayNumber >= start && t.dayNumber <= end && t.result !== "pending",
      );
      return Math.round((resolved.length / 7) * 100);
    };

    const totalWeeks = s?.weeks ?? 4;
    const weekColors = ["#a78bfa", "#38bdf8", "#34d399", "#fbbf24"];
    const weeks = Array.from({ length: totalWeeks }, (_, i) => ({
      label: `Week ${i + 1} · Analysis`,
      pct: weekProgress(i + 1),
      tone: weekColors[i] ?? "#a78bfa",
    }));

    const recentCalls = (s?.trades ?? []).slice().reverse().slice(0, 4);

    // Unlocked success screen
    if (s?.unlocked) {
      return (
        <div
          style={{
            animation: "szFade .5s ease both",
            textAlign: "center",
            maxWidth: 700,
            margin: "0 auto",
            padding: "60px 0",
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 24 }}>🏆</div>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(32px,4.4vw,52px)",
              fontWeight: 800,
              letterSpacing: "-.038em",
            }}
          >
            Futures{" "}
            <span
              style={{
                background:
                  "linear-gradient(100deg,#7c3aed,#6d28d9 46%,#2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Unlocked!
            </span>
          </h1>
          <p
            style={{
              margin: "16px auto 0",
              maxWidth: 460,
              fontSize: 16,
              lineHeight: 1.6,
              color: "#6a6580",
            }}
          >
            You achieved a {s.winRate}% win rate across {s.wins + s.losses}{" "}
            predictions. You now have full access to Futures signals.
          </p>
          <button
            className="sz-btn-primary"
            style={{ ...S.primary, marginTop: 36 }}
            onClick={() => navigate("/dashboard")}
          >
            Enter Signals Room →
          </button>

          {/* Certification request */}
          <div style={{ marginTop: 32, padding: "24px 28px", background: "linear-gradient(135deg,#faf8ff,#f3f0ff)", border: "1px solid #e4d9fb", borderRadius: 18, maxWidth: 420, margin: "32px auto 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-certificate" style={{ color: "#fff", fontSize: 16 }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: "-.01em" }}>Certification</div>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "#6a6580", lineHeight: 1.6 }}>
              You've proven your skills. Request your official certificate — the admin will review and approve it.
            </p>
            {certStatus === "approved" ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(5,150,105,.1)", border: "1px solid rgba(52,211,153,.4)", borderRadius: 10, padding: "10px 16px" }}>
                <i className="fa-solid fa-circle-check" style={{ color: "#059669", fontSize: 16 }} />
                <span style={{ fontWeight: 800, fontSize: 13.5, color: "#059669" }}>Certificate Approved!</span>
              </div>
            ) : certStatus === "pending" ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 10, padding: "10px 16px" }}>
                <i className="fa-solid fa-clock" style={{ color: "#b45309", fontSize: 14 }} />
                <span style={{ fontWeight: 700, fontSize: 13.5, color: "#b45309" }}>Request Sent — Awaiting Approval</span>
              </div>
            ) : (
              <button
                className="sz-btn-green"
                disabled={certLoading}
                onClick={handleRequestCertification}
                style={{ padding: "11px 24px", borderRadius: 12, border: "none", cursor: certLoading ? "not-allowed" : "pointer", fontWeight: 800, fontSize: 14, background: certLoading ? "#a7f3d0" : "linear-gradient(135deg,#059669,#047857)", color: "#fff", boxShadow: "0 8px 20px rgba(5,150,105,.3)", transition: "all .2s ease" }}
              >
                {certLoading ? "Submitting…" : "Request Certificate"}
              </button>
            )}
          </div>
        </div>
      );
    }

    // Session failed screen
    if (s?.sessionComplete && !s?.unlocked) {
      return (
        <div
          style={{
            animation: "szFade .5s ease both",
            textAlign: "center",
            maxWidth: 600,
            margin: "0 auto",
            padding: "60px 0",
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 20 }}>📉</div>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(28px,3.8vw,44px)",
              fontWeight: 800,
              letterSpacing: "-.03em",
            }}
          >
            Session Complete
          </h1>
          <p
            style={{
              margin: "14px auto 0",
              maxWidth: 420,
              fontSize: 15,
              lineHeight: 1.6,
              color: "#6a6580",
            }}
          >
            You finished with a <strong>{s.winRate}%</strong> win rate ({s.wins}{" "}
            wins / {s.losses} losses). You need ≥62% to unlock Futures. Try
            again — your skills improve with practice.
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
              marginTop: 32,
            }}
          >
            <button
              className="sz-btn-primary"
              disabled={actionLoading}
              style={{ ...S.primary, opacity: actionLoading ? 0.7 : 1 }}
              onClick={handleRetryDemo}
            >
              {actionLoading ? "Starting…" : "Try Again →"}
            </button>
            <button
              className="sz-btn-ghost"
              style={S.ghost}
              onClick={() => setScreen("courses")}
            >
              ← Back to Courses
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ animation: "szFade .5s ease both" }}>
        {/* heading */}
        <div style={{ maxWidth: 620, marginBottom: 34 }}>
          <div
            style={{
              ...S.pill,
              background: "rgba(16,185,129,.1)",
              borderColor: "rgba(16,185,129,.3)",
              color: "#059669",
              marginBottom: 18,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#10b981",
                boxShadow: "0 0 10px rgba(16,185,129,.8)",
              }}
            />
            Unlocked
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(30px,3.9vw,46px)",
              fontWeight: 800,
              letterSpacing: "-.034em",
              lineHeight: 1.05,
            }}
          >
            Demo Market{" "}
            <span
              style={{
                background:
                  "linear-gradient(100deg,#7c3aed,#6d28d9 46%,#2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Analysis
            </span>
          </h1>
          <p
            style={{
              margin: "13px 0 0",
              fontSize: 15,
              lineHeight: 1.6,
              color: "#6a6580",
            }}
          >
            Call the market every day for one month. No capital at risk — your
            reasoning is what gets graded.
          </p>
        </div>

        {/* two-col */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr .9fr",
            gap: 22,
            alignItems: "start",
          }}
        >
          {/* LEFT: prediction card */}
          <div
            style={{
              ...S.card,
              overflow: "hidden",
              borderColor: "rgba(167,139,250,.28)",
              boxShadow: "0 26px 66px rgba(31,20,66,.1)",
            }}
          >
            {/* card header */}
            <div
              style={{
                padding: "26px 28px 22px",
                borderBottom: "1px solid #f0edf8",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    color: "#9a94ad",
                    marginBottom: 7,
                  }}
                >
                  TODAY'S CHALLENGE · DAY {s?.currentDay ?? 1}
                </div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    letterSpacing: "-.02em",
                    lineHeight: 1.3,
                  }}
                >
                  Will Bitcoin be HIGHER or LOWER after 4 Hours?
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 26,
                    fontWeight: 800,
                    letterSpacing: "-.03em",
                  }}
                >
                  {livePrice ? fmtPrice(livePrice) : "Loading…"}
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#059669",
                  }}
                >
                  {btcPrice ? "● Live BTC/USDT" : "BTC/USDT"}
                </div>
              </div>
            </div>

            {/* form body */}
            <div style={{ padding: "26px 28px 30px" }}>
              {s?.submittedToday ? (
                (() => {
                  const latestTrade = s?.trades?.length
                    ? s.trades[s.trades.length - 1]
                    : null;
                  const isResolved =
                    latestTrade && latestTrade.result !== "pending";
                  const minsLeft = s?.resolvesAt
                    ? Math.ceil((new Date(s.resolvesAt) - Date.now()) / 60000)
                    : 0;
                  const timeLeft =
                    minsLeft >= 60
                      ? `${Math.floor(minsLeft / 60)}h ${minsLeft % 60}m`
                      : `${minsLeft}m`;
                  const nextAt = s?.resolvesAt
                    ? new Date(s.resolvesAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null;

                  if (isResolved) {
                    const isWin = latestTrade.result === "win";
                    return (
                      <div style={{ textAlign: "center", padding: "32px 0" }}>
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: 18,
                            margin: "0 auto 18px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isWin
                              ? "rgba(5,150,105,.1)"
                              : "rgba(220,38,38,.1)",
                            border: `1.5px solid ${isWin ? "rgba(52,211,153,.4)" : "rgba(248,113,113,.4)"}`,
                          }}
                        >
                          <i
                            className={`fa-solid ${isWin ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}`}
                            style={{
                              fontSize: 26,
                              color: isWin ? "#059669" : "#dc2626",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 800,
                            marginBottom: 6,
                            color: isWin ? "#059669" : "#dc2626",
                          }}
                        >
                          Day {latestTrade.dayNumber} — {isWin ? "WIN" : "LOSS"}
                        </div>
                        <p
                          style={{
                            fontSize: 14,
                            color: "#6a6580",
                            margin: "6px auto 0",
                            maxWidth: 300,
                            lineHeight: 1.6,
                          }}
                        >
                          Analysis resolved. Your next analysis will be
                          available after the cooldown expires.
                        </p>
                        {nextAt && (
                          <div
                            style={{
                              marginTop: 18,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              background: "#f6f2ff",
                              border: "1px solid #e0d5fb",
                              borderRadius: 10,
                              padding: "10px 16px",
                            }}
                          >
                            <span style={{ fontSize: 15 }}>⏱</span>
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: "#7c3aed",
                              }}
                            >
                              Next analysis in {timeLeft} &nbsp;·&nbsp; {nextAt}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div style={{ textAlign: "center", padding: "32px 0" }}>
                      <div style={{ fontSize: 44, marginBottom: 16 }}>⏳</div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 800,
                          marginBottom: 10,
                        }}
                      >
                        Analysis Submitted!
                      </div>
                      <p
                        style={{
                          fontSize: 14,
                          color: "#6a6580",
                          margin: "0 auto",
                          maxWidth: 320,
                          lineHeight: 1.6,
                        }}
                      >
                        Your analysis is being evaluated. Check back in a few
                        hours to see the result and submit your next one.
                      </p>
                      {nextAt && (
                        <div
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#9a94ad",
                            marginTop: 14,
                          }}
                        >
                          Resolves around {nextAt}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <>
                  {/* Higher / Lower */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 13,
                      marginBottom: 26,
                    }}
                  >
                    {["higher", "lower"].map((dir) => {
                      const active = demoForm.direction === dir;
                      const isH = dir === "higher";
                      return (
                        <button
                          key={dir}
                          className="sz-dir-btn"
                          onClick={() =>
                            setDemoForm((f) => ({ ...f, direction: dir }))
                          }
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: 24,
                            borderRadius: 18,
                            cursor: "pointer",
                            transition: "all .24s ease",
                            background: active
                              ? isH
                                ? "linear-gradient(160deg,rgba(16,185,129,.16),rgba(5,150,105,.08))"
                                : "linear-gradient(160deg,rgba(248,113,113,.14),rgba(220,38,38,.07))"
                              : "#fff",
                            border: active
                              ? isH
                                ? "1.5px solid rgba(16,185,129,.6)"
                                : "1.5px solid rgba(220,38,38,.55)"
                              : "1.5px solid #e5e0f2",
                            color: active
                              ? isH
                                ? "#059669"
                                : "#dc2626"
                              : "#6a6580",
                            boxShadow: active
                              ? isH
                                ? "0 14px 34px rgba(5,150,105,.16)"
                                : "0 14px 34px rgba(220,38,38,.14)"
                              : "none",
                          }}
                        >
                          <span style={{ fontSize: 28, lineHeight: 1 }}>
                            {isH ? "↑" : "↓"}
                          </span>
                          <span
                            style={{
                              fontSize: 16,
                              fontWeight: 800,
                              letterSpacing: ".04em",
                            }}
                          >
                            {isH ? "Higher" : "Lower"}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Confidence */}
                  <div style={{ marginBottom: 26 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: ".16em",
                          textTransform: "uppercase",
                          color: "#7c3aed",
                        }}
                      >
                        Confidence
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 20,
                          fontWeight: 800,
                          letterSpacing: "-.02em",
                        }}
                      >
                        {demoForm.confidence}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={demoForm.confidence}
                      onChange={(e) =>
                        setDemoForm((f) => ({
                          ...f,
                          confidence: Number(e.target.value),
                        }))
                      }
                      style={{
                        width: "100%",
                        height: 6,
                        borderRadius: 999,
                        outline: "none",
                        appearance: "none",
                        WebkitAppearance: "none",
                        background: `linear-gradient(90deg,#7c3aed ${demoForm.confidence}%,#e5e0f2 ${demoForm.confidence}%)`,
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontFamily: "monospace",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".12em",
                        color: "#a9a4bd",
                        marginTop: 9,
                      }}
                    >
                      <span>UNSURE</span>
                      <span>CERTAIN</span>
                    </div>
                  </div>

                  {/* Analysis */}
                  <div style={{ marginBottom: 24 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: ".16em",
                        textTransform: "uppercase",
                        color: "#2563eb",
                        marginBottom: 11,
                      }}
                    >
                      Explain your analysis
                    </div>
                    <textarea
                      rows={4}
                      placeholder="What is your reasoning? Reference structure, volume, news, or risk."
                      value={demoForm.analysis}
                      onChange={(e) =>
                        setDemoForm((f) => ({ ...f, analysis: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        fontFamily: "inherit",
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        color: "#141020",
                        background: "#fff",
                        border: "1.5px solid #e5e0f2",
                        borderRadius: 16,
                        padding: "16px 18px",
                        resize: "vertical",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontFamily: "monospace",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".1em",
                        color: "#a9a4bd",
                        marginTop: 8,
                      }}
                    >
                      <span>
                        {demoForm.analysis.trim().length < 12
                          ? "MIN 12 CHARACTERS"
                          : "✓ READY"}
                      </span>
                      <span>{demoForm.analysis.trim().length} CHARS</span>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    className="sz-btn-green"
                    disabled={isSubmitLocked}
                    onClick={handleDemoSubmit}
                    style={{
                      width: "100%",
                      textAlign: "center",
                      fontSize: 15,
                      fontWeight: 800,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      padding: 19,
                      borderRadius: 16,
                      transition: "all .24s ease",
                      ...(isSubmitLocked
                        ? {
                            color: "#b0aac4",
                            background: "#fbfbfd",
                            border: "1px solid #eeebf6",
                            cursor: "not-allowed",
                          }
                        : {
                            color: "#fff",
                            border: "none",
                            cursor: "pointer",
                            background:
                              "linear-gradient(135deg,#34d399,#059669)",
                            boxShadow: "0 16px 40px rgba(5,150,105,.34)",
                          }),
                    }}
                  >
                    {actionLoading
                      ? "Submitting…"
                      : isSubmitLocked
                        ? "Pick a direction and explain it"
                        : "Submit Analysis"}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: sidebar */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* 4-week progress */}
            <div
              style={{ ...S.card, padding: "24px 26px", background: "#fff" }}
            >
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#9a94ad",
                  marginBottom: 18,
                }}
              >
                4-WEEK PROGRAM
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {weeks.map((w, i) => (
                  <div key={i}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 7,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: w.pct > 0 ? "#141020" : "#8a86a0",
                        }}
                      >
                        {w.label}
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: "#a9a4bd",
                        }}
                      >
                        {w.pct}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: 5,
                        borderRadius: 999,
                        background: "#eceaf2",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 999,
                          width: `${w.pct}%`,
                          background: `linear-gradient(90deg,${w.tone}88,${w.tone})`,
                          transition: "width .9s cubic-bezier(.3,.8,.3,1)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div
              style={{ ...S.card, padding: "18px 22px", background: "#fff" }}
            >
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#9a94ad",
                  marginBottom: 14,
                }}
              >
                SESSION STATS · {(s?.wins ?? 0) + (s?.losses ?? 0)}/
                {s?.totalDays ?? 28} DAYS
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                }}
              >
                {[
                  { label: "Wins", value: s?.wins ?? 0, color: "#059669" },
                  { label: "Losses", value: s?.losses ?? 0, color: "#dc2626" },
                  {
                    label: "Win Rate",
                    value: `${s?.winRate ?? 0}%`,
                    color: s?.winRate >= 62 ? "#059669" : "#7c3aed",
                  },
                ].map((stat, i) => (
                  <div key={i} style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: 22,
                        fontWeight: 800,
                        color: stat.color,
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: ".1em",
                        textTransform: "uppercase",
                        color: "#a9a4bd",
                        marginTop: 4,
                      }}
                    >
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent calls */}
            <div
              style={{ ...S.card, padding: "24px 26px", background: "#fff" }}
            >
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#9a94ad",
                  marginBottom: 16,
                }}
              >
                RECENT CALLS
              </div>
              {recentCalls.length === 0 ? (
                <div
                  style={{
                    fontSize: 13,
                    color: "#a9a4bd",
                    textAlign: "center",
                    padding: "12px 0",
                  }}
                >
                  No analyses yet — submit your first one!
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {recentCalls.map((t, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 800,
                          flexShrink: 0,
                          background:
                            t.direction === "higher"
                              ? "rgba(16,185,129,.1)"
                              : "rgba(248,113,113,.1)",
                          color:
                            t.direction === "higher" ? "#059669" : "#dc2626",
                        }}
                      >
                        {t.direction === "higher" ? "↑" : "↓"}
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          flex: 1,
                          fontSize: 12,
                          color: "#6a6580",
                        }}
                      >
                        Day {t.dayNumber} · BTC
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: "#9a94ad",
                        }}
                      >
                        {t.confidence}%
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: ".1em",
                          borderRadius: 6,
                          padding: "4px 8px",
                          color:
                            t.result === "win"
                              ? "#059669"
                              : t.result === "loss"
                                ? "#dc2626"
                                : "#b45309",
                          background:
                            t.result === "win"
                              ? "rgba(16,185,129,.1)"
                              : t.result === "loss"
                                ? "rgba(248,113,113,.1)"
                                : "rgba(245,158,11,.1)",
                        }}
                      >
                        {t.result === "win"
                          ? "WIN"
                          : t.result === "loss"
                            ? "LOSS"
                            : "PENDING"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              className="sz-btn-ghost"
              style={{
                ...S.ghost,
                width: "100%",
                textAlign: "center",
                borderRadius: 14,
              }}
              onClick={() => setScreen("courses")}
            >
              ← Back to Courses
            </button>
          </div>
        </div>

        {/* day grid */}
        <div style={{ marginTop: 22, ...S.card, padding: "20px 24px" }}>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: ".18em",
              textTransform: "uppercase",
              color: "#9a94ad",
              marginBottom: 14,
            }}
          >
            {s?.totalDays ?? 28}-DAY PROGRESS ·{" "}
            {(s?.wins ?? 0) + (s?.losses ?? 0)}/{s?.totalDays ?? 28} DAYS
            COMPLETE · TARGET ≥62% WIN RATE
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Array.from({ length: s?.totalDays ?? 28 }).map((_, i) => {
              const trade = (s?.trades ?? []).find(
                (t) => t.dayNumber === i + 1,
              );
              const bg = !trade
                ? "#eceaf2"
                : trade.result === "win"
                  ? "#34d399"
                  : trade.result === "loss"
                    ? "#f87171"
                    : "#fbbf24";
              return (
                <div
                  key={i}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "monospace",
                    fontSize: 9,
                    fontWeight: 800,
                    color: trade ? "#fff" : "#a9a4bd",
                  }}
                >
                  {i + 1}
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginTop: 14,
              fontFamily: "monospace",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".1em",
              color: "#a9a4bd",
              flexWrap: "wrap",
            }}
          >
            {[
              ["#34d399", "WIN"],
              ["#f87171", "LOSS"],
              ["#fbbf24", "PENDING"],
              ["#eceaf2", "FUTURE"],
            ].map(([c, l]) => (
              <span
                key={l}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: c,
                  }}
                />
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const SCREEN_MAP = {
    landing: renderLanding,
    roadmap: renderRoadmap,
    courses: renderCourses,
    demo: renderDemo,
  };

  return (
    <div style={S.stage}>
      <div style={S.pad}>
        {/* header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            flexWrap: "wrap",
            marginBottom: 40,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={S.logo}>WZ</div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "-.01em",
                }}
              >
                Waqar Zaka Trades
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#9a94ad",
                }}
              >
                {screen === "landing"
                  ? "Choose your path"
                  : screen === "roadmap"
                    ? "Learning journey"
                    : screen === "demo"
                      ? "Demo market analysis"
                      : "Lecture library"}
              </div>
            </div>
          </div>
          <button
            className="sz-btn-back"
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#9a94ad",
              background: "transparent",
              border: "1px solid #e5e0f2",
              borderRadius: 999,
              padding: "9px 15px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* screen content */}
        {(SCREEN_MAP[screen] || renderLanding)()}
      </div>

      {/* video modal */}
      {renderVideoModal()}

      {/* warning modal */}
      {warningOpen && renderWarningModal()}

      {/* centered toast popup */}
      {toast && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 998,
              background: "rgba(20,10,40,.35)",
              backdropFilter: "blur(6px)",
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              zIndex: 999,
              animation: "szToastIn .28s cubic-bezier(.3,.8,.3,1) both",
              pointerEvents: "none",
              width: "90%",
              maxWidth: 400,
            }}
          >
            <div
              style={{
                background: "#fff",
                border: "1px solid #ede8fb",
                borderRadius: 24,
                padding: "40px 36px 36px",
                boxShadow:
                  "0 32px 80px rgba(124,61,237,.18), 0 8px 24px rgba(0,0,0,.08)",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 20,
                  margin: "0 auto 20px",
                  background:
                    "linear-gradient(135deg,rgba(52,211,153,.15),rgba(5,150,105,.08))",
                  border: "1.5px solid rgba(52,211,153,.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                }}
              >
                ✓
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 800,
                  color: "#141020",
                  letterSpacing: "-.02em",
                  marginBottom: 10,
                }}
              >
                Analysis Submitted!
              </div>
              <div style={{ fontSize: 14, color: "#6a6580", lineHeight: 1.6 }}>
                {toast}
              </div>
              <div
                style={{
                  marginTop: 22,
                  height: 4,
                  borderRadius: 99,
                  background: "#f0edf8",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 99,
                    background: "linear-gradient(90deg,#8b5cf6,#6d28d9)",
                    animation: "szToastBar 3s linear both",
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
